import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { injectIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { getCRMFiltersOptions } from 'crm/_api/api';
import { filterText } from './messages';
import { clearConditions, getCondition, PAYMENT_PERIOD, paymentPeriodFilter, serializeParams } from './model';

const ContactsFiltersContext = createContext(null);
export const useContactsFilters = () => useContext(ContactsFiltersContext);

const ContactsFiltersProvider = ({ children, intl }) => {
  const history = useHistory();
  const location = useLocation();
  const params = useMemo(() => {
    const next = { page_size: '10', ...queryString.parse(location.search) };
    if (typeof next.start_end_date === 'string') {
      const dates = next.start_end_date.split('_');
      if (dates.length === 2) {
        [next.payments_from, next.payments_to] = dates;
        delete next.start_end_date;
      }
    }
    return next;
  }, [location.search]);
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editor, setEditor] = useState(null);
  const text = key => filterText(intl, key);
  const replace = next => history.replace({ ...location, search: serializeParams(next) });
  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getCRMFiltersOptions();
      if (response.status >= 300) throw new Error();
      setFilters({ ...response.data, [PAYMENT_PERIOD]: paymentPeriodFilter });
    } catch (e) {
      setError(text('error'));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const change = (key, condition) => {
    const filter = filters[key];
    if (!filter) return;
    const next = { ...params, page: 1 };
    if (key === PAYMENT_PERIOD) {
      delete next.payments_from;
      delete next.payments_to;
      delete next.start_end_date;
      if (condition) {
        if (condition.operator !== 'lte') next.payments_from = condition.values[0];
        if (condition.operator !== 'gte') next.payments_to = condition.values[condition.values.length - 1];
      }
      replace(next);
      return;
    }
    delete next[filter.query_param];
    delete next[`${filter.query_param}__op`];
    if (condition) {
      next[filter.query_param] = condition.values;
      next[`${filter.query_param}__op`] = condition.operator;
    }
    replace(next);
  };
  const reset = () => replace({ ...clearConditions(params), page: 1 });
  const active = Object.entries(filters)
    .map(([key, filter]) => ({ key, filter, condition: getCondition(filter, params) }))
    .filter(item => item.condition);
  const value = useMemo(
    () => ({ intl, text, params, filters, loading, error, editor, active, setEditor, change, reset, replace, load }),
    [intl, params, filters, loading, error, editor, location, history]
  );
  return <ContactsFiltersContext.Provider value={value}>{children}</ContactsFiltersContext.Provider>;
};

ContactsFiltersProvider.propTypes = { children: PropTypes.node.isRequired, intl: PropTypes.object.isRequired };
export default injectIntl(ContactsFiltersProvider);
